/**
 * One colour per manager, for anywhere a GM is plotted rather than merely named.
 *
 * Ten hues at even intervals at one fixed lightness — `hsl(H 58% 44%)` — so no two managers'
 * lines can be confused on a chart and none of them shouts louder than the others. Fixing the
 * lightness is what makes that true: picking ten colours by eye gives you three that vanish on
 * white and one that dominates every chart it appears in.
 *
 * TWO USES, AND NO MORE (the design handoff is explicit): a 2px ring on the avatar, and the stroke
 * of that GM's line or sparkline. It is deliberately NOT a fill, a text colour or a row tint —
 * ten saturated backgrounds would turn every table into a paint chart, and the ring is enough to
 * tie a row to its line.
 *
 * The default avatar goes neutral grey underneath, with the ring carrying identity. That replaces
 * the pale blue every default face used to share, which said "this is a face" rather than "this is
 * Rajan".
 */

/** The hue per manager. Even intervals, assigned once and then never reshuffled — a GM's colour
 *  is his, and re-sorting the league would otherwise repaint everybody's history. */
const HUES: Readonly<Record<string, number>> = {
  SAMANT: 12,
  TIM: 35,
  VISHAL: 58,
  JOHN: 95,
  RYAN: 150,
  AMIT: 182,
  MATT: 205,
  RAJAN: 232,
  ARI: 268,
  LUKE: 310,
};

/** Saturation and lightness are constants, not parameters. See the note above. */
const SAT = 58;
const LIGHT = 44;

/** Somebody not on the list — a new manager, a viewer — gets the neutral face and no ring. */
export const GM_NEUTRAL = '#cbd3e0';

export function gmHue(gm: string): number | null {
  return HUES[gm.toUpperCase()] ?? null;
}

export function gmColor(gm: string): string {
  const h = gmHue(gm);
  return h === null ? GM_NEUTRAL : `hsl(${h} ${SAT}% ${LIGHT}%)`;
}

/** Every manager who has a colour, for a legend or a test. */
export const COLOURED_GMS: readonly string[] = Object.keys(HUES);

/** The neutral face the ring sits on. */
export const FACE_FILL = '#eef1f6';
export const FACE_INK = '#55607a';

/** Two letters, uppercase — what a ringed circle holds when there is no photograph. */
export const initials = (name: string): string => name.slice(0, 2).toUpperCase();
